import { Col, Row } from 'react-bootstrap';
import { LogDisplayItem } from '../../../types/interface';
import Timeline from 'components/base/Timeline';
import {
  UilCheckCircle,
  UilTimesCircle,
  UilClock,
  UilCommentAlt,
  UilEye,
  UilEdit,
  UilSignin,
  UilRedo
} from '@iconscout/react-unicons';
import 'components/base/applicationComments.scss';

interface LogConfig {
  icon: JSX.Element;
  dotClass: string;
  badgeClass: string;
}

const getLogConfig = (type: string): LogConfig => {
  const upper = type?.toUpperCase() ?? '';

  switch (true) {
    case upper.includes('DELETE'):
      return {
        icon: <UilTimesCircle className="text-danger" size={14} />,
        dotClass: 'border-danger',
        badgeClass: 'badge-status badge-rejected'
      };
    case upper.includes('CREATE') || upper.includes('ADD'):
      return {
        icon: <UilCheckCircle className="text-success" size={14} />,
        dotClass: 'border-success',
        badgeClass: 'badge-status badge-approved'
      };
    case upper.includes('UPDATE') || upper.includes('EDIT'):
      return {
        icon: <UilEdit className="text-primary" size={14} />,
        dotClass: 'border-primary',
        badgeClass: 'badge-status badge-underreview'
      };
    case upper.includes('VIEW') || upper.includes('ACCESS'):
      return {
        icon: <UilEye className="text-info" size={14} />,
        dotClass: 'border-info',
        badgeClass: 'badge-status badge-feedback'
      };
    case upper.includes('PENDING'):
      return {
        icon: <UilClock className="text-warning" size={14} />,
        dotClass: 'border-warning',
        badgeClass: 'badge-status badge-pending'
      };
    case upper.includes('APPROVED') || upper.includes('APPROVE'):
      return {
        icon: <UilCheckCircle className="text-success" size={14} />,
        dotClass: 'border-success',
        badgeClass: 'badge-status badge-approved'
      };
    case upper.includes('REJECTED') || upper.includes('REJECT'):
      return {
        icon: <UilTimesCircle className="text-danger" size={14} />,
        dotClass: 'border-danger',
        badgeClass: 'badge-status badge-rejected'
      };
    case upper.includes('FEEDBACK'):
      return {
        icon: <UilCommentAlt className="text-info" size={14} />,
        dotClass: 'border-info',
        badgeClass: 'badge-status badge-feedback'
      };
    case upper.includes('RESUBMIT'):
      return {
        icon: <UilRedo className="text-warning" size={14} />,
        dotClass: 'border-warning',
        badgeClass: 'badge-status badge-pending'
      };
    case upper.includes('LOGIN'):
      return {
        icon: <UilSignin className="log-icon-muted" size={14} />,
        dotClass: 'border-translucent',
        badgeClass: 'badge-status badge-default'
      };
    default:
      return {
        icon: <UilCommentAlt className="log-icon-muted" size={14} />,
        dotClass: 'border-translucent',
        badgeClass: 'badge-status badge-default'
      };
  }
};

const formatType = (type: string): string => {
  if (!type) return '';
  return (
    type.charAt(0).toUpperCase() +
    type.slice(1).toLowerCase().replace(/[_-]/g, ' ')
  );
};

const groupByDate = (
  items: LogDisplayItem[]
): { date: string; items: LogDisplayItem[] }[] => {
  const grouped: Record<string, LogDisplayItem[]> = {};
  items.forEach(item => {
    const key = item.date || 'Unknown';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });
  return Object.entries(grouped).map(([date, items]) => ({ date, items }));
};

const LogTimeline = ({
  data,
  idPrefix
}: {
  data: LogDisplayItem[];
  idPrefix?: string;
}) => {
  if (!data.length) return null;

  const groups = groupByDate(data);

  return (
    <>
      {groups.map(group => (
        <div key={group.date}>
          <h4 className="py-2 border-y mb-3">{group.date}</h4>
          <Timeline variant="basic" className="mb-4">
            {group.items.map((item, i) => {
              const { icon, dotClass, badgeClass } = getLogConfig(item.type);
              const isLast = i === group.items.length - 1;
              return (
                <Timeline.Item
                  key={`${item.id}-${i}`}
                  id={idPrefix ? `${idPrefix}-${item.id}` : undefined}
                >
                  <Row className="g-3">
                    <Col xs="auto">
                      <Timeline.Separator className="position-relative">
                        <Timeline.Dot
                          className={`icon-item-md border bg-body ${dotClass}`}
                        >
                          {icon}
                        </Timeline.Dot>
                        {!isLast && (
                          <Timeline.Bar className="h-100 border-dashed" />
                        )}
                      </Timeline.Separator>
                    </Col>
                    <Col>
                      <Timeline.Content>
                        <div
                          className={`comment-card rounded-2 p-3 ${
                            !isLast ? 'mb-3' : ''
                          }`}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className={badgeClass}>
                              {formatType(item.type)}
                            </span>
                            <span className="text-body-tertiary fs-10 text-nowrap ms-3">
                              {item.timestamp}
                            </span>
                          </div>
                          {item.userName && (
                            <p className="mb-1 fs-10 text-body-tertiary">
                              {item.userName}
                            </p>
                          )}
                          {item.message && (
                            <p className="mb-0 text-body-secondary fs-9 mt-1">
                              {item.message}
                            </p>
                          )}
                          {item.comment && (
                            <p className="mb-0 text-body-tertiary fs-9 mt-1 fst-italic">
                              {item.comment}
                            </p>
                          )}
                        </div>
                      </Timeline.Content>
                    </Col>
                  </Row>
                </Timeline.Item>
              );
            })}
          </Timeline>
        </div>
      ))}
    </>
  );
};

export default LogTimeline;
